import { Injectable } from '@angular/core';
import { getLocation, getNodeValue, Node, parseTree } from 'jsonc-parser';

import { Environment, RecordComparisonItem } from '../../components/env-sync/types';
import { DiffLine } from './types';

/**
 * Generates diff lines (GitHub-style) for JSON data when comparing QA vs. Production.
 * Highlights the exact lines where differences occur, including nested structures.
 *
 * Uses a JSON AST (via `jsonc-parser`) instead of raw string search because:
 *  - Handles nested objects, arrays, and multi-line values correctly.
 *  - Finds exact node positions for precise line highlighting.
 *  - Avoids false positives from similar keys/values in unrelated locations.
 */
@Injectable({
  providedIn: 'root',
})
export class JsonDiffService {
  /**
   * Creates a list of `DiffLine` objects for the given environment.
   * @param record Comparison record with both QA and Prod JSON.
   * @param env Environment to generate the diff for.
   */
  generateDiffLines(record: RecordComparisonItem, env: Environment): DiffLine[] {
    const data = env === Environment.QA ? record.qa?.data : record.prod?.data;

    if (!data) {
      return [
        {
          type: 'unchanged',
          content: 'null',
          lineNumber: 1,
        },
      ];
    }

    const { _metadata, ...filteredData } = data;
    const formattedJson = JSON.stringify(filteredData, null, 2);
    const jsonLines = formattedJson.split('\n');

    if (!record.diff?.length) {
      return jsonLines.map((line, i) => ({
        type: 'unchanged',
        content: line,
        lineNumber: i + 1,
      }));
    }

    const rootNode = parseTree(formattedJson);
    if (!rootNode) {
      return [
        {
          type: 'removed',
          content: 'ERROR: Unable to parse JSON',
          lineNumber: 1,
        },
      ];
    }

    const changedLines = new Set<number>();
    for (const change of record.diff) {
      const lines = this.findLinesForPath(change.path, rootNode, formattedJson);
      lines.forEach((line) => changedLines.add(line));
    }

    const changeType = env === Environment.QA ? 'added' : 'removed';
    return jsonLines.map((line, i) => ({
      type: changedLines.has(i + 1) ? changeType : 'unchanged',
      content: line,
      lineNumber: i + 1,
    }));
  }

  /**
   * Finds all line numbers affected by a change at the given JSON path.
   * @param path Path segments to the changed node.
   * @param node Root AST node.
   * @param json Full JSON string.
   */
  private findLinesForPath(path: (string | number)[], node: Node, json: string): number[] {
    const changedNode = this.findNodeByPath(node, path);
    if (!changedNode) {
      return [];
    }

    const location = getLocation(json, changedNode.offset);

    // CASE 1: Property values (key + value on same or multiple lines)
    // Highlights the whole property for consistency.
    if (!location.isAtPropertyKey && changedNode.parent?.type === 'property') {
      const propertyNode = changedNode.parent;
      const propertyLocation = getLocation(json, propertyNode.offset);

      const isRootProperty = propertyLocation.path.length > 0;
      const startLine = isRootProperty
        ? this.getLineForOffset(json, propertyNode.offset)
        : this.getLineForOffset(json, changedNode.offset);

      const endLine = this.getLineForOffset(json, changedNode.offset + changedNode.length - 1);
      return this.getLinesInRange(startLine, endLine);
    }

    // CASE 2: Structural nodes (objects/arrays)
    // Highlights entire structure, incl. braces/brackets.
    if (changedNode.type === 'object' || changedNode.type === 'array') {
      const startLine = this.getLineForOffset(json, changedNode.offset);
      const endLine = this.getLineForOffset(json, changedNode.offset + changedNode.length - 1);
      return this.getLinesInRange(startLine, endLine);
    }

    // CASE 3: Primitive values (standalone in arrays or root)
    // Highlights only the value’s line.
    return [this.getLineForOffset(json, changedNode.offset)];
  }

  /**
   * Locates a JSON AST node by its path.
   * Path may include object keys (string) or array indices (number).
   * Enables mapping a diff path directly to a node's exact position.
   * @param node - The current AST node.
   * @param path - The remaining path segments (keys or array indices).
   */
  private findNodeByPath(node: Node, path: (string | number)[]): Node | null {
    // If we've reached the end of the path, this is the node we want
    if (path.length === 0) {
      return node;
    }

    // If the node has no children, we can't go further
    if (!node.children) {
      return null;
    }

    const [currentSegment, ...remainingPath] = path;

    // Object key lookup
    if (node.type === 'object') {
      for (const child of node.children) {
        if (child.type === 'property' && child.children && child.children.length >= 2) {
          const keyNode = child.children[0];
          const valueNode = child.children[1];
          if (keyNode.type === 'string' && getNodeValue(keyNode) === currentSegment) {
            if (remainingPath.length === 0) {
              return valueNode;
            }
            return this.findNodeByPath(valueNode, remainingPath);
          }
        }
      }
    }
    // Array index lookup
    else if (node.type === 'array') {
      const index = Number(currentSegment);
      if (index >= 0 && index < node.children.length) {
        const elementNode = node.children[index];
        if (remainingPath.length === 0) {
          return elementNode;
        }
        return this.findNodeByPath(elementNode, remainingPath);
      }
    }

    // No match found
    return null;
  }

  /** Returns the line number for a given character offset in the JSON string. */
  private getLineForOffset(json: string, offset: number): number {
    return json.substring(0, offset).split('\n').length;
  }

  /** Returns all line numbers in the inclusive range. */
  private getLinesInRange(start: number, end: number): number[] {
    const lines: number[] = [];
    for (let i = start; i <= end; i++) {
      lines.push(i);
    }
    return lines;
  }
}
